// src/App.js
import React, { useMemo, useState } from "react";

const IN = 96; // px/in
const PAGE_W_IN = 8.5;
const PAGE_H_IN = 11;

// Fixed anchors
const SUMMARY_TOP_IN = 4.0;
const CONTENT_L_IN = 0.5;
const CONTENT_R_IN = 0.5;

const PAGE1_TOP_IN = SUMMARY_TOP_IN;
const PAGE_NUMBER_BASELINE_IN  = 0.1875; // 3/16" from bottom
const GAP_ABOVE_PAGE_NUMBER_IN = 0.25;  // keep 0.25" above page number
const PAGE1_BOTTOM_IN_NO_BOTTOM_INSERT = PAGE_H_IN - (PAGE_NUMBER_BASELINE_IN + GAP_ABOVE_PAGE_NUMBER_IN);

const PAGE_N_TOP_IN = 0.75;   // subsequent pages top margin
const PAGE_N_BOTTOM_IN = 0.5; // subsequent pages bottom margin
const BOTTOM_INSERT_HEIGHT_IN = 2.0; // bottom marketing block visual height
const SAFE_GAP_ABOVE_BOTTOM_INSERT_IN = 0.125; // was 0.25; tighter but safe
const BOTTOM_INSERT_RESERVED_IN = BOTTOM_INSERT_HEIGHT_IN + PAGE_N_BOTTOM_IN + SAFE_GAP_ABOVE_BOTTOM_INSERT_IN;
const PAGE1_BOTTOM_IN_WITH_BOTTOM_INSERT = PAGE_H_IN - BOTTOM_INSERT_RESERVED_IN;

// CS/Website placement
const CSBOX_HEIGHT_IN = 0.5;
const CSBOX_GAP_IN = 0.25;
const CSBOX_TOP_IN = SUMMARY_TOP_IN - CSBOX_GAP_IN - CSBOX_HEIGHT_IN; // 3.25"

// Spacing between stacked sections (mb-3 ~ 12px ≈ 0.125")
const SECTION_SPACING_IN = 0.1;

// Helpers
function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 0, g: 0, b: 0 };
}
function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex || "#000000");
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function App() {
  // --- Campaign Manager Image (marketing) ---
  const [marketingImagePosition, setMarketingImagePosition] = useState("none"); // "none" | "top" | "bottom" | "both"
  const [marketingRadius, setMarketingRadius] = useState(12); // px
  // --- Header logo ---
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoDims, setLogoDims] = useState(null); // {w,h}

  // --- CS / Website box ---
  const [csBoxFillColor, setCsBoxFillColor] = useState("#ffffff");
  const [csBoxBorderColor, setCsBoxBorderColor] = useState("#cbd5e1");
  const [csBoxRadius, setCsBoxRadius] = useState(10);
  const [csColumnsCount, setCsColumnsCount] = useState(2); // 1..3
  const [csColumns, setCsColumns] = useState([
    { title: "Customer Service", value: "877-838-2345" },
    { title: "Website", value: "hc3.io" },
    { title: "", value: "" },
  ]);
  const csBoxRef = React.useRef(null);
  const [csMeasuredIn, setCsMeasuredIn] = useState(CSBOX_HEIGHT_IN);
  React.useLayoutEffect(() => {
    const el = csBoxRef.current;
    if (!el) return;
    const hIn = el.getBoundingClientRect().height / IN;
    if (Math.abs(hIn - csMeasuredIn) > 0.01) setCsMeasuredIn(hIn);
  }, [csColumnsCount, csColumns, csBoxRadius, csBoxFillColor, csBoxBorderColor]);

  // --- Global section header style ---
  const [headerColor, setHeaderColor] = useState("#000000"); // default black
  const [headerPt, setHeaderPt] = useState(11);              // default 11pt

  // --- Account Summary (layout + colors) ---
  const [summaryFillColor, setSummaryFillColor] = useState("#ffffff");
  const [summaryBorderColor, setSummaryBorderColor] = useState("#e5e7eb");
  const [summaryRadius, setSummaryRadius] = useState(12);
  const [summaryLayout, setSummaryLayout] = useState("cards"); // "cards" | "condensed"

  // Summary typography & row spacing
  const [summaryFontPt, setSummaryFontPt] = useState(10);
  const [summaryRowPadPx, setSummaryRowPadPx] = useState(8);

  // Mini-card style controls
  const [tileFillColor, setTileFillColor] = useState("#ffffff");
  const [tileBorderColor, setTileBorderColor] = useState("#e5e7eb");
  const [tileScale, setTileScale] = useState(1.0); // 0.8 .. 1.2

  // Measured summary height (for precise pagination)
  const [summaryMeasuredIn, setSummaryMeasuredIn] = useState(null);

  // Chart options
  const [chartType, setChartType] = useState("none"); // "none" | "donut" | "bar"
  const [chartSide, setChartSide] = useState("right"); // "right" | "left"
  const [chartSize, setChartSize] = useState(180); // px
  const [creditColor, setCreditColor] = useState("#60a5fa");
  const [debitColor, setDebitColor] = useState("#1e3a8a");
  const [chartAlpha, setChartAlpha] = useState(0.9);

  // Tables (Activity / Daily) compaction
  const [tableScale, setTableScale] = useState(1.0); // 0.8 .. 1.1
  const [measuredRowIn, setMeasuredRowIn] = useState(null);

  // Activity layout mode (controls only; not shown on page)
  const [activityMode, setActivityMode] = useState("debits-credits"); // "debits-credits" | "amount-balance"

  // Section box styles for Activity & Daily
  const [activityFill, setActivityFill] = useState("#ffffff");
  const [activityBorder, setActivityBorder] = useState("#e5e7eb");
  const [activityRadius, setActivityRadius] = useState(10);

  const [dailyFill, setDailyFill] = useState("#ffffff");
  const [dailyBorder, setDailyBorder] = useState("#e5e7eb");
  const [dailyRadius, setDailyRadius] = useState(10);

  // NEW: Advanced table appearance controls (per section)
  const densityPad = (d) => (d === "compact" ? 2 : d === "comfy" ? 8 : 4);
  const [activityTableOpt, setActivityTableOpt] = useState({
    headerBg: "#FFFFFF",
    headerFg: "#000000",
    separatorStyle: "none", // none | solid | dashed
    separatorColor: "#FFFFFF",
    separatorThickness: 1, // px
    zebra: false,
    zebraColor: "#F3F4F6",
    zebraOpacity: 0.9,
    vAlign: "middle", // top | middle | bottom
    density: "compact", // compact | cozy | comfy
    hdrSeparatorStyle: "none",
    hdrSeparatorColor: "#E5E7EB",
    hdrSeparatorThickness: 0,
  
    fontPt: 10,
    rowPadPx: 6});
  const [dailyTableOpt, setDailyTableOpt] = useState({
    headerBg: "#FFFFFF",
    headerFg: "#000000",
    separatorStyle: "none",
    separatorColor: "#FFFFFF",
    separatorThickness: 1,
    zebra: false,
    zebraColor: "#F3F4F6",
    zebraOpacity: 0.9,
    vAlign: "middle",
    density: "compact",
    hdrSeparatorStyle: "none",
    hdrSeparatorColor: "#E5E7EB",
    hdrSeparatorThickness: 0,
  
    fontPt: 10,
    rowPadPx: 6});

  const hasBottomMarketing =
    marketingImagePosition === "bottom" || marketingImagePosition === "both";

  const page1BottomIn = hasBottomMarketing
    ? PAGE1_BOTTOM_IN_WITH_BOTTOM_INSERT
    : PAGE1_BOTTOM_IN_NO_BOTTOM_INSERT;

  // ---- Activity dataset: 17 generic rows with HC3Bank&Trust ----
  // [date, description, debit, credit, balance]
  const activityRows = useMemo(
    () => [
      ["7/01", "FITRANSFER HC3Bank&Trust WEB", "", "35.00", "191.49"],
      ["7/02", "ATM Deposit HC3Bank&Trust", "", "20.00", "211.49"],
      ["7/03", "Card Purchase GROCERIES", "12.18", "", "199.31"],
      ["7/05", "Bill Pay UTILITIES", "45.00", "", "154.31"],
      ["7/07", "ExternalTransfer HC3Bank&Trust", "", "25.00", "179.31"],
      ["7/10", "FITRANSFER HC3Bank&Trust WEB", "", "20.00", "199.31"],
      ["7/11", "ACH Payroll", "", "75.00", "274.31"],
      ["7/12", "Card Purchase GAS", "31.50", "", "242.81"],
      ["7/15", "Card Purchase DINING", "5.00", "", "237.81"],
      ["7/18", "ExternalTransfer HC3Bank&Trust", "", "18.25", "256.06"],
      ["7/19", "Online Transfer TO SAVINGS", "40.00", "", "216.06"],
      ["7/20", "ACH Refund", "", "8.75", "224.81"],
      ["7/22", "Card Purchase RETAIL", "14.32", "", "210.49"],
      ["7/24", "Check #1023", "41.82", "", "168.67"],
      ["7/27", "Cash Deposit", "", "20.00", "188.67"],
      ["7/29", "Service Charge", "3.50", "", "185.17"],
      ["7/31", "Interest Credit", "", "2.00", "187.17"],
    ],
    []
  );

  // Totals for chart
  const totals = useMemo(() => {
    let deb = 0, cred = 0;
    for (const r of activityRows) {
      if (r[2]) deb += parseFloat(r[2]);
      if (r[3]) cred += parseFloat(r[3]);
    }
    return { debits: deb, credits: cred };
  }, [activityRows]);

  // ---- Donut chart (SVG) ----
  const DonutChart = ({ debits, credits, sizePx, debitC, creditC }) => {
    const total = Math.max(0.0001, debits + credits);
    const fracCredits = credits / total;
    const fracDebits = debits / total;
    const cx = sizePx / 2, cy = sizePx / 2;
    const r = sizePx * 0.35, stroke = sizePx * 0.22;
    const C = 2 * Math.PI * r;
    const creditLen = C * fracCredits, debitLen = C * fracDebits;
    const rotate = `rotate(-90 ${cx} ${cy})`;
    return (
      <div className="flex flex-col items-center justify-center">
        <svg width={sizePx} height={sizePx} viewBox={`0 0 ${sizePx} ${sizePx}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} transform={rotate} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={creditC} strokeWidth={stroke}
            strokeDasharray={`${creditLen} ${C - creditLen}`} strokeDashoffset={0} transform={rotate} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={debitC} strokeWidth={stroke}
            strokeDasharray={`${debitLen} ${C - debitLen}`} strokeDashoffset={-creditLen} transform={rotate} />
          <circle cx={cx} cy={cy} r={r - stroke / 2} fill="white" />
        </svg>
        <div className="mt-1 text-[9pt]">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-[2px]" style={{ background: debitC }} /> Debits
            <span className="ml-3 inline-block w-3 h-3 rounded-[2px]" style={{ background: creditC }} /> Credits
          </div>
        </div>
      </div>
    );
  };

  // ---- Bar chart (SVG) ----
  const BarChart = ({ debits, credits, sizePx, debitC, creditC }) => {
    const w = sizePx, h = sizePx * 0.8;
    const maxVal = Math.max(debits, credits, 0.0001);
    const pad = 16;
    const barW = (w - pad * 3) / 2;
    const debH = (debits / maxVal) * (h - pad * 2);
    const credH = (credits / maxVal) * (h - pad * 2);
    return (
      <div className="flex flex-col items-center justify-center">
        <svg width={w} height={h}>
          <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#e5e7eb" />
          <rect x={pad} y={h - pad - debH} width={barW} height={debH} fill={debitC} rx="4" />
          <rect x={pad * 2 + barW} y={h - pad - credH} width={barW} height={credH} fill={creditC} rx="4" />
        </svg>
        <div className="mt-1 text-[9pt]">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-[2px]" style={{ background: debitC }} /> Debits
            <span className="ml-3 inline-block w-3 h-3 rounded-[2px]" style={{ background: creditC }} /> Credits
          </div>
        </div>
      </div>
    );
  };

  // ---- Summary height estimate (in) ----
  const summaryEstIn = useMemo(() => {
    if (summaryLayout === "condensed") return 1.3;
    return 2.8 + (tileScale - 1.0) * 1.2;
  }, [summaryLayout, tileScale]);

  // ---- Activity sizing helpers (in) ----
  const rowIn = (measuredRowIn ?? (0.28 * tableScale)) + 0.0125; // never undercount by adding tiny safety
  const tableHeaderIn = 0.4;       // thead height
  const titleIn = Math.max(0.26, 0.26 + (headerPt - 11) * 0.02);
  const boxPadIn = 0.3;
  const activityOverheadIn = titleIn + tableHeaderIn + boxPadIn;
  const dailyHeightIn = 0.35 + (6 * rowIn) + 0.25;

  // ---- Dynamic page-1 top offset (accommodate CS box actual height) ----
  const page1TopIn = Math.max(PAGE1_TOP_IN, CSBOX_TOP_IN + csMeasuredIn + CSBOX_GAP_IN);
  // ---- Build pagination plan ----
  const pagesPlan = useMemo(() => {
    // Use Cards-style summary height for planning when condensed is selected
    const CARDS_SUMMARY_EST_IN = 2.8 + (tileScale - 1.0) * 1.2; // kept for reference
    const summaryPlanIn = summaryMeasuredIn ?? summaryEstIn;

    const pages = [];
    const page1Avail = page1BottomIn - page1TopIn;
    let remain = page1Avail;

    // Page 1
    const p1 = { summary: true, activityRows: [], daily: false };
    remain -= summaryPlanIn + SECTION_SPACING_IN;

    // Gate: only start Activity on p1 if we can fit header + at least 5 rows; then pack as many as possible
    let rowsLeft = activityRows.length;
    const MIN_P1_ROWS = hasBottomMarketing ? 1 : 3;
    if (remain > activityOverheadIn + MIN_P1_ROWS * rowIn) {
      const epsilonIn = 0.01;
      const rowsFit = Math.max(0, Math.floor(((remain - activityOverheadIn) - epsilonIn) / rowIn));
      const take = Math.min(rowsFit, rowsLeft);
      p1.activityRows = activityRows.slice(0, take);
      rowsLeft -= take;
      remain -= activityOverheadIn + take * rowIn + SECTION_SPACING_IN;
    }

    // Daily only after all activity rows are placed

    // FINAL GUARD: ensure page 1 content fits within available height (0.25" rule)
    const epsilonIn = 0.01;
    const predictP1 = () => {
      let h = 0;
      h += summaryPlanIn + SECTION_SPACING_IN;
      if (p1.activityRows.length) h += activityOverheadIn + p1.activityRows.length * rowIn + SECTION_SPACING_IN;
      if (p1.daily) h += dailyHeightIn + SECTION_SPACING_IN;
      return h;
    };
    while (predictP1() > page1Avail - epsilonIn && p1.activityRows.length > 0) {
      rowsLeft += 1;
      p1.activityRows = p1.activityRows.slice(0, -1);
      remain += rowIn; // bookkeeping for remain
    }

    if (rowsLeft === 0 && remain >= dailyHeightIn) {
      p1.daily = true;
      remain -= dailyHeightIn + SECTION_SPACING_IN;
    }
    pages.push(p1);

    // Subsequent pages (activity continue, then daily)
    while (rowsLeft > 0) {
      const avail = PAGE_H_IN - PAGE_N_TOP_IN - PAGE_N_BOTTOM_IN;
      const rowsFit = Math.max(1, Math.floor((avail - activityOverheadIn) / rowIn));
      const take = Math.min(rowsFit, rowsLeft);
      pages.push({
        summary: false,
        activityRows: activityRows.slice(activityRows.length - rowsLeft, activityRows.length - rowsLeft + take),
        daily: false,
      });
      rowsLeft -= take;
    }

    // Try to place Daily on the last page; else add one
    const last = pages[pages.length - 1];
    const lastAvail = last.summary
      ? (page1BottomIn - PAGE1_TOP_IN) - summaryPlanIn - SECTION_SPACING_IN - (last.activityRows.length ? (activityOverheadIn + last.activityRows.length * rowIn + SECTION_SPACING_IN) : 0)
      : (PAGE_H_IN - PAGE_N_TOP_IN - PAGE_N_BOTTOM_IN) - (last.activityRows.length ? (activityOverheadIn + last.activityRows.length * rowIn + SECTION_SPACING_IN) : 0);
    if (!last.daily) {
      if (lastAvail >= dailyHeightIn) last.daily = true;
      else pages.push({ summary: false, activityRows: [], daily: true });
    }
    return pages;
  }, [
    activityRows,
    activityOverheadIn,
    dailyHeightIn,
    page1BottomIn,
    page1TopIn,
    rowIn,
    summaryEstIn,
    summaryMeasuredIn,
  ]);

  // ---- Locked header (page 1 only) ----
  const logoLayout = useMemo(() => {
    if (!logoDims) return "vertical";
    const r = logoDims.w / logoDims.h;
    return r >= 2.5 ? "vertical" : "horizontal";
  }, [logoDims]);

  const LockedHeader = () => (
    <>
      {logoLayout === "vertical" ? (
        <>
          {/* Wide logo: max 2.0" x 0.5", address beneath */}
          <div
            className="absolute select-none pointer-events-none"
            style={{
              top: `${0.25 * IN}px`,
              left: `${0.875 * IN}px`,
              width: `${2.0 * IN}px`,
              height: `${0.5 * IN}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
            title="Client Logo (Locked)"
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Client Logo"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                onLoad={(ev) => {
                  const img = ev.currentTarget;
                  setLogoDims({ w: img.naturalWidth, h: img.naturalHeight });
                }}
              />
            ) : (
              <div className="w-full h-full bg-gray-300 rounded-sm flex items-center justify-center text-[10px] text-gray-700">
                Upload Logo
              </div>
            )}
          </div>
          <div
            className="absolute leading-[1.25em] select-none pointer-events-none"
            style={{
              top: `${(0.25 + 0.5 + 0.1) * IN}px`,
              left: `${0.875 * IN}px`,
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: "8pt",
            }}
            title="Return Address (Locked)"
          >
            <div>PO Box 12345</div>
            <div>Anywhere, AL 12345-6789</div>
          </div>
        </>
      ) : (
        // Tall/square logo: ≤0.75" high; address to right; total width ≤ 2.0"
        <div
          className="absolute select-none pointer-events-none"
          style={{
            top: `${0.25 * IN}px`,
            left: `${0.875 * IN}px`,
            width: `${2.0 * IN}px`,
            height: `${0.75 * IN}px`,
            display: "flex",
            alignItems: "center",
          }}
          title="Logo + Return (Locked)"
        >
          <div style={{ maxWidth: "45%", marginRight: `${0.08 * IN}px`, height: "100%", display: "flex", alignItems: "center" }}>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Client Logo"
                style={{ maxHeight: `${0.75 * IN}px`, maxWidth: "100%", objectFit: "contain", display: "block" }}
                onLoad={(ev) => {
                  const img = ev.currentTarget;
                  setLogoDims({ w: img.naturalWidth, h: img.naturalHeight });
                }}
              />
            ) : (
              <div className="w-full h-[0.75in] bg-gray-300 rounded-sm flex items-center justify-center text-[10px] text-gray-700">
                Upload Logo
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: "8pt",
              lineHeight: "1.25em",
            }}
          >
            <div>PO Box 12345</div>
            <div>Anywhere, AL 12345-6789</div>
          </div>
        </div>
      )}
    </>
  );

  // ---- Campaign images ----
  const renderMarketing = () => {
    const topBlock = (
      <div
        className="absolute top-[0.85in] right-[0.5in] w-[3in] h-[2.25in] bg-blue-300 flex items-center justify-center text-xs"
        style={{ borderRadius: `${marketingRadius}px` }}
      >
        Campaign Manager Image (Top)
      </div>
    );
    const bottomBlock = (
      <div
        className="absolute bottom-[0.5in] left-[0.5in] right-[0.5in] h-[2in] bg-blue-300 flex items-center justify-center text-xs"
        style={{ borderRadius: `${marketingRadius}px` }}
      >
        Campaign Manager Image (Bottom)
      </div>
    );
    if (marketingImagePosition === "top") return topBlock;
    if (marketingImagePosition === "bottom") return bottomBlock;
    if (marketingImagePosition === "both")
      return (
        <>
          {topBlock}
          {bottomBlock}
        </>
      );
    return null;
  };

  // ---- Sections ----

  const SummarySection = () => {
    const summaryRef = React.useRef(null);
    React.useLayoutEffect(() => {
      const el = summaryRef.current;
      if (!el) return;
      const hIn = el.getBoundingClientRect().height / IN;
      // Avoid thrash: only update if change is meaningful
      if (!summaryMeasuredIn || Math.abs(hIn - summaryMeasuredIn) > 0.02) {
        setSummaryMeasuredIn(hIn);
      }
    }, [summaryLayout, tileScale, chartType, chartSide, chartSize, headerPt, headerColor, summaryFillColor, summaryBorderColor, summaryRadius, tileFillColor, tileBorderColor, summaryFontPt, summaryRowPadPx]);

    const tileFontPt = summaryFontPt;
    const tilePadPx = summaryRowPadPx;

    const debitRGBA = rgba(debitColor, chartAlpha);
    const creditRGBA = rgba(creditColor, chartAlpha);

    const Chart =
      chartType === "donut" ? (
        <DonutChart debits={totals.debits} credits={totals.credits} sizePx={chartSize} debitC={debitRGBA} creditC={creditRGBA} />
      ) : chartType === "bar" ? (
        <BarChart debits={totals.debits} credits={totals.credits} sizePx={chartSize} debitC={debitRGBA} creditC={creditRGBA} />
      ) : null;

    const ChartColumn = Chart ? (
      <div style={{ width: `${chartSize}px` }} className="hidden lg:block">
        {Chart}
      </div>
    ) : null;

    const SummaryBody =
      summaryLayout === "cards" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" style={{ fontSize: `${tileFontPt}pt` }}>
          {[
            ["Beginning Balance", "$156.49"],
            ["Ending Balance", "$225.00"],
            ["Days in Period", "28"],
            ["Deposits/Credits (4)", "$80.69"],
            ["Checks/Debits (1)", "$12.18"],
            ["Interest Paid", "$0.00"],
            ["Service Charge", "$0.00"],
            ["Average Ledger", "$213.27"],
            ["Average Collected", "$213.27"],
          ].map(([label, val], i) => (
            <div
              key={i}
              
              style={{
                backgroundColor: tileFillColor,
                borderColor: tileBorderColor,
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: `${summaryRadius}px`,
                padding: `${tilePadPx}px`,
              }}
            >
              <div className="font-semibold">{label}</div>
              <div>{val}</div>
            </div>
          ))}
        </div>
      ) : (
        // Condensed layout
        <div className="grid grid-cols-2 gap-x-6" style={{ fontSize: `${summaryFontPt}pt` }}>
          <>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Beginning Balance</span><span>$156.49</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Days in Statement Period</span><span>28</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Deposits/Credits (4)</span><span>$80.69</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Average Ledger</span><span>$213.27</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Checks/Debits (1)</span><span>$12.18</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Average Collected</span><span>$213.27</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Interest Paid</span><span>$0.00</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Service Charge</span><span>$0.00</span></div>
            <div className="flex justify-between" style={{ padding: `${summaryRowPadPx/2}px 0` }}><span>Ending Balance</span><span>$225.00</span></div>
          </>
        </div>
      );

    return (
      <div ref={summaryRef}
        className="border p-3 mb-3"
        style={{
          backgroundColor: summaryFillColor,
          borderColor: summaryBorderColor,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: `${summaryRadius}px`,
        }}
        title="Account Summary"
      >
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
          <div className="font-semibold" style={{ color: headerColor, fontSize: `${headerPt}pt` }}>
            Checking Account - XXXXXX1234
          </div>
          <div className="text-[10pt] text-gray-700">Statement Period: 06/23/2025 – 07/20/2025</div>
        </div>

        {/* Content + optional chart (left/right) */}
        {chartType !== "none" && chartSide === "left" ? (
          <div className="flex gap-4">
            {ChartColumn}
            <div className="flex-1 max-w-full">{SummaryBody}</div>
          </div>
        ) : chartType !== "none" && chartSide === "right" ? (
          <div className="flex gap-4">
            <div className="flex-1 max-w-full">{SummaryBody}</div>
            {ChartColumn}
          </div>
        ) : (
          SummaryBody
        )}
      </div>
    );
  };

  // Measure an actual table row height to improve pagination accuracy
  function ActivityRowProbe() {
    const rowRef = React.useRef(null);
    React.useLayoutEffect(() => {
      const tr = rowRef.current;
      if (!tr) return;
      const hIn = tr.getBoundingClientRect().height / IN;
      if (!measuredRowIn || Math.abs(hIn - measuredRowIn) > 0.01) {
        setMeasuredRowIn(hIn);
      }
    }, [tableScale, activityTableOpt.fontPt, activityTableOpt.rowPadPx, activityTableOpt.density]);
    const fsPt = (activityTableOpt.fontPt ?? 10);
    const padPx = padFor(activityTableOpt);
    return (
      <table style={{ position: "absolute", visibility: "hidden", left: -9999, top: -9999, fontSize: `${fsPt}pt` }}>
        <tbody>
          <tr ref={rowRef}>
            <td data-valign={activityTableOpt.vAlign} style={tdPad(activityTableOpt, padPx)}><span className="cell">7/01</span></td>
            <td data-valign={activityTableOpt.vAlign} style={tdPad(activityTableOpt, padPx)}><span className="cell">Probe Row</span></td>
            <td data-valign={activityTableOpt.vAlign} className="text-right" style={tdPad(activityTableOpt, padPx)}><span className="cell">-1.00</span></td>
            <td data-valign={activityTableOpt.vAlign} className="text-right" style={tdPad(activityTableOpt, padPx)}><span className="cell">1.00</span></td>
            <td data-valign={activityTableOpt.vAlign} className="text-right" style={tdPad(activityTableOpt, padPx)}><span className="cell">100.00</span></td>
          </tr>
        </tbody>
      </table>
    );
  }

  // Reusable renderer for table header cell style based on opts
  const thStyle = (opts) => ({
    backgroundColor: opts.headerBg,
    color: opts.headerFg,
    borderBottomStyle: opts.hdrSeparatorStyle === "none" ? "solid" : opts.hdrSeparatorStyle,
    borderBottomWidth: opts.hdrSeparatorStyle === "none" ? 0 : opts.hdrSeparatorThickness,
    borderBottomColor: opts.hdrSeparatorColor,
  });

  const tdStyleBase = (opts) => ({
    lineHeight: "1.25em",
    borderBottomStyle: opts.separatorStyle === "none" ? "solid" : opts.separatorStyle,
    borderBottomWidth: opts.separatorStyle === "none" ? 0 : opts.separatorThickness,
    borderBottomColor: opts.separatorColor,
  });

  const vAlignFlex = (v) => (v === "top" ? "flex-start" : v === "bottom" ? "flex-end" : "center");

  // Distribute top/bottom padding to simulate vertical alignment in both web and export
  const tdPad = (opts, padPx) => {
    const base = Math.max(0, padPx|0);
    let top = base, bottom = base;
    if (opts.vAlign === "top") { top = Math.round(base * 0.25); bottom = Math.round(base * 1.75); }
    else if (opts.vAlign === "bottom") { top = Math.round(base * 1.75); bottom = Math.round(base * 0.25); }
    else if (opts.vAlign === "middle") { top = Math.round(base * 1.1); bottom = Math.max(0, (base * 2) - top); }
    return { paddingTop: `${top}px`, paddingBottom: `${bottom}px`, paddingLeft: `${base}px`, paddingRight: `${base}px` };
  };


  const padFor = (opts) => {
    if (typeof opts.rowPadPx === "number") return Math.max(0, opts.rowPadPx);
    const densPad = densityPad(opts.density);
    const scalePad = 4 * tableScale; // previous base
    return densPad + scalePad;
  };

  // Activity table (slice)
  function ActivityTable({ rows }) {
    const fsPt = (activityTableOpt.fontPt ?? 10);
    const padPx = padFor(activityTableOpt);
    const zebraBg = activityTableOpt.zebra ? rgba(activityTableOpt.zebraColor, activityTableOpt.zebraOpacity) : "transparent";

    return (
      <div
        className="p-3 mb-3"
        title="Account Activity"
        style={{
          backgroundColor: activityFill,
          borderColor: activityBorder,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: `${activityRadius}px`,
        }}
      >
        <div className="font-semibold mb-2" style={{ fontSize: `${headerPt}pt`, color: headerColor }}>
          Account Activity
        </div>
        <div className="overflow-x-auto"><table className="w-full" style={{ fontSize: `${fsPt}pt`, borderCollapse: "collapse", borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="text-left" style={{ padding: `${padPx}px`, ...thStyle(activityTableOpt) }}>
                Date
              </th>
              <th className="text-left" style={{ padding: `${padPx}px`, ...thStyle(activityTableOpt) }}>Description</th>
              {activityMode === "debits-credits" ? (
                <>
                  <th className="text-right" style={{ padding: `${padPx}px`, ...thStyle(activityTableOpt) }}>Debits</th>
                  <th className="text-right" style={{ padding: `${padPx}px`, ...thStyle(activityTableOpt) }}>Credits</th>
                </>
              ) : (
                <th className="text-right" style={{ padding: `${padPx}px`, ...thStyle(activityTableOpt) }}>Amount</th>
              )}
              <th className="text-right" style={{ padding: `${padPx}px`, ...thStyle(activityTableOpt) }}>
                {activityMode === "debits-credits" ? "Balance" : "Balance"}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const rowBg = idx % 2 === 1 ? zebraBg : "transparent";
              if (activityMode === "debits-credits") {
                return (
                  <tr key={idx} style={{ background: rowBg }}>
                    <td data-valign={activityTableOpt.vAlign} style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[0]}</span></td>
                    <td data-valign={activityTableOpt.vAlign} style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[1]}</span></td>
                    <td data-valign={activityTableOpt.vAlign} className="text-right" style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[2] ? "-" + row[2] : ""}</span></td>
                    <td data-valign={activityTableOpt.vAlign} className="text-right" style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[3] ?? ""}</span></td>
                    <td data-valign={activityTableOpt.vAlign} className="text-right" style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[4]}</span></td>
                  </tr>
                );
              } else {
                const debit = row[2] ? parseFloat(row[2]) : 0;
                const credit = row[3] ? parseFloat(row[3]) : 0;
                const amount = credit - debit;
                return (
                  <tr key={idx} style={{ background: rowBg }}>
                    <td data-valign={activityTableOpt.vAlign} style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[0]}</span></td>
                    <td data-valign={activityTableOpt.vAlign} style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[1]}</span></td>
                    <td data-valign={activityTableOpt.vAlign} className="text-right" style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{amount < 0 ? "-" : ""}{Math.abs(amount).toFixed(2)}</span></td>
                    <td data-valign={activityTableOpt.vAlign} className="text-right" style={{ ...tdStyleBase(activityTableOpt), ...tdPad(activityTableOpt, padPx) }}><span className="cell">{row[4]}</span></td>
                  </tr>
                );
              }
            })}
          </tbody>
        </table></div>
      </div>
    );
  }

  const DailySection = () => {
    const fsPt = (dailyTableOpt.fontPt ?? 10);
    const padPx = padFor(dailyTableOpt);
    const zebraBg = dailyTableOpt.zebra ? rgba(dailyTableOpt.zebraColor, dailyTableOpt.zebraOpacity) : "transparent";

    const tdS = { ...tdStyleBase(dailyTableOpt), ...tdPad(dailyTableOpt, padPx) };

    return (
      <div
        className="p-3 mb-3"
        title="Daily Balance"
        style={{
          backgroundColor: dailyFill,
          borderColor: dailyBorder,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: `${dailyRadius}px`,
        }}
      >
        <div className="font-semibold mb-2" style={{ fontSize: `${headerPt}pt`, color: headerColor }}>
          Daily Balance
        </div>
        <div className="grid grid-cols-2 gap-4 overflow-x-auto" style={{ fontSize: `${fsPt}pt` }}>
          {[0, 1].map((col) => (
            <table key={col} className="w-full" style={{ borderCollapse: "collapse", borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th className="text-left" style={{ padding: `${padPx}px`, ...thStyle(dailyTableOpt) }}>Date</th>
                  <th className="text-right" style={{ padding: `${padPx}px`, ...thStyle(dailyTableOpt) }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(col === 0
                  ? [
                      ["6/23", "212.18"],
                      ["7/03", "200.00"],
                      ["7/10", "220.00"],
                    ]
                  : [
                      ["7/15", "225.00"],
                      ["7/22", "210.49"],
                      ["7/31", "187.17"],
                    ]
                ).map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 1 ? zebraBg : "transparent" }}>
                    <td data-valign={dailyTableOpt.vAlign} style={tdS}><span className="cell">{r[0]}</span></td>
                    <td data-valign={dailyTableOpt.vAlign} className="text-right" style={tdS}><span className="cell">{r[1]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </div>
    );
  };

  // ---- Page shell ----
  function Barcode2D() {
    return (
      <svg width="100%" height="100%" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="22" height="22" fill="#fff"/>
        <rect x="1" y="1" width="6" height="6" fill="#000"/>
        <rect x="15" y="1" width="6" height="6" fill="#000"/>
        <rect x="1" y="15" width="6" height="6" fill="#000"/>
        <rect x="9" y="2" width="2" height="2" fill="#000"/>
        <rect x="12" y="4" width="1" height="1" fill="#000"/>
        <rect x="10" y="7" width="3" height="3" fill="#000"/>
        <rect x="8" y="12" width="2" height="2" fill="#000"/>
        <rect x="12" y="12" width="1" height="1" fill="#000"/>
        <rect x="15" y="9" width="2" height="2" fill="#000"/>
        <rect x="9" y="16" width="3" height="3" fill="#000"/>
        <rect x="13" y="17" width="2" height="2" fill="#000"/>
      </svg>
    );
  }

  function Page({ number, pageCount, showMarketing, showHeaderLocked, showMailWindow, children }) {
    return (
      <div
        className="page-shell relative ml-auto bg-white shadow rounded border border-gray-300 overflow-hidden"
        style={{ width: `${PAGE_W_IN}in`, height: `${PAGE_H_IN}in` }}
      >
        {showHeaderLocked && <LockedHeader />}

        {/* 2D barcode (LOCKED on every page) */}
        <div className="absolute top-[0.375in] left-[7.85in] w-[0.229in] h-[0.229in] select-none pointer-events-none">
          <Barcode2D />
        </div>

        {/* Page 1 mailing marks */}
        {showMailWindow && (
          <>
            {/* 2D barcode for inserting equipment */}
            <div className="absolute top-[2.10in] left-[0.75in] w-[0.229in] h-[0.229in] select-none pointer-events-none">
              <Barcode2D />
            </div>

            {/* Address block with keyline */}
            <div
              className="absolute top-[1.925in] left-[1.25in] leading-[1.2em] select-none pointer-events-none"
              style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "8pt" }}
            >
              <div>123456-12B**012345</div>
              <div>John Doe</div>
              <div>123 Main St.</div>
              <div>City, ST 98765-4321</div>
            </div>
          </>
        )}

        {/* Campaign images */}
        {showMarketing && renderMarketing()}

        {/* CS/Website box (always above summary) */}
        {number === 1 && (
          <div
            className="absolute border px-3"
            style={{
              zIndex: 10,
              top: `${CSBOX_TOP_IN * IN}px`,
              right: `${0.5 * IN}px`,
              width: `${3.0 * IN}px`,
              minHeight: `${CSBOX_HEIGHT_IN * IN}px`,
              backgroundColor: csBoxFillColor,
              borderColor: csBoxBorderColor,
              borderRadius: `${csBoxRadius}px`,
              borderWidth: "1px",
              borderStyle: "solid",
              display: "flex",
              alignItems: "center",
            }}
            title="Customer Service / Website"
          ref={csBoxRef}>
            <div className="w-full grid text-center" style={{ fontSize: "11px", lineHeight: "1.25", rowGap: "2px", gridTemplateColumns: `repeat(${csColumnsCount}, 1fr)` }}>
  {csColumns.slice(0, csColumnsCount).map((c, i) => (
    <div key={`title-${i}`} className="font-semibold whitespace-nowrap">{c.title || "\u00A0"}</div>
  ))}
  {csColumns.slice(0, csColumnsCount).map((c, i) => (
    <div key={`value-${i}`} className="">{c.value || "\u00A0"}</div>
  ))}
</div>
          </div>
        )}
        {/* Flow area */}
        <div
          className="absolute"
          style={{ zIndex: 5,
            left: `${CONTENT_L_IN * IN}px`,
            right: `${CONTENT_R_IN * IN}px`,
            top: `${(number === 1 ? page1TopIn : PAGE_N_TOP_IN) * IN}px`,
            bottom: `${(number === 1 ? (PAGE_H_IN - page1BottomIn) : PAGE_N_BOTTOM_IN) * IN}px`,
            overflow: "hidden",
          }}
        >
          {children}
        </div>

        {/* Page number (Arial 8pt; 3/16" from bottom, 0.5" from right) */}
        <div
          className="absolute"
          style={{ zIndex: 5,
            right: `${0.5 * IN}px`,
            bottom: `${0.1875 * IN}px`,
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: "8pt",
          }}
        >
          Page {number} of {pageCount}
        </div>
      </div>
    );
  }
  // --------------------------
  // Sections to render by page
  // --------------------------

  // Build pages from plan
  const pages = [];
  pagesPlan.forEach((plan, i) => {
    const pageNum = i + 1;
    const isP1 = pageNum === 1;
    const sections = [];

    if (plan.summary) sections.push(<SummarySection key={`sum-${i}`} />);
    if (plan.activityRows && plan.activityRows.length) {
      sections.push(<ActivityTable key={`act-${i}`} rows={plan.activityRows} />);
    }
    if (plan.daily) sections.push(<DailySection key={`day-${i}`} />);

    pages.push(
      <Page
        key={`p-${i}`}
        number={pageNum}
        pageCount={pagesPlan.length}
        showMarketing={isP1 && marketingImagePosition !== "none"}
        showHeaderLocked={isP1}
        showMailWindow={isP1}
      >
        {sections}
      </Page>
    );
  });

  // =======================
  // Controls (clean layout)
  // =======================
  const TAB_DEF = [
    { key: "header", label: "Header (Logo & Return)" },
    { key: "mkt",    label: "Campaign Manager Image" },
    { key: "cs",     label: "Customer Service / Website" },
    { key: "hdrsty", label: "Section Header Style" },
    { key: "summary",label: "Account Summary" },
    { key: "chart",  label: "Summary Chart (optional)" },
    { key: "activity", label: "Account Activity Style" },
    { key: "daily",  label: "Daily Balance Style" },
    { key: "export", label: "Export / Settings" },  ];
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("sb.activeTab") || "mkt"; } catch { return "mkt"; }
  });
  React.useEffect(() => {
    try { localStorage.setItem("sb.activeTab", activeTab); } catch {}
  }, [activeTab]);

  // ===== Export: Standalone HTML (Letter 8.5x11, centered) =====

  // Convert <img> to data URLs so the HTML file is self-contained
  async function imgToDataURL(imgEl) {
    try {
      if (String(imgEl.src).startsWith("data:")) return imgEl.src;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.decoding = "sync";
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = imgEl.src;
      });
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL("image/png");
    } catch {
      return imgEl.src;
    }
  }

  // Clone .page-shell nodes and embed assets
  async function clonePageHTML(node) {
    const clone = node.cloneNode(true);
    // nice preview spacing on-screen
    clone.style.margin = "0 auto 1rem";
    const imgs = Array.from(clone.querySelectorAll("img"));
    for (const el of imgs) {
      el.src = await imgToDataURL(el);
    }
    return clone.outerHTML;
  }

  async function exportToHTML() {
    const pageNodes = Array.from(document.querySelectorAll(".page-shell"));
    if (!pageNodes.length) {
      alert("No pages to export.");
      return;
    }
    const clonedHtml = [];
    for (const n of pageNodes) {
      clonedHtml.push(await clonePageHTML(n));
    }

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Statement Export</title>
<script src="https://cdn.tailwindcss.com"></script>
<style>
  /* Letter page setup + centered preview */
  @page { size: ${PAGE_W_IN}in ${PAGE_H_IN}in; margin: 0.5in; }
  :root { --w: ${PAGE_W_IN}in; --h: ${PAGE_H_IN}in; }
  html, body { height: 100%; }
  body { margin: 0; background: #eef2f7; padding: 1rem; }
  .stack { width: max-content; margin: 0 auto; }
  .page-shell {
    box-shadow: 0 0.1in 0.3in rgba(0,0,0,.18);
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    background: #fff;
  }
  @media print {
    body { background: none; padding: 0; }
    .page-shell { box-shadow: none !important; border-radius: 0 !important; border: none !important; margin: 0 auto !important; }
    .page-shell:not(:last-child) { page-break-after: always; }
  }
</style>
</head>
<body>
  <div class="stack">
    ${clonedHtml.join("\\n")}
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "statement.html";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);
  }

  // ===== Settings (save / load) =====
  const SETTINGS_KEY = "sb.settings.v1";

  function saveSettings() {
    const payload = {
      // Marketing
      marketingImagePosition, marketingRadius,
      // CS Box
      csBoxFillColor, csBoxBorderColor, csBoxRadius,
      csColumnsCount, csColumns,
      // Headers
      headerColor, headerPt,
      // Summary
      summaryFillColor, summaryBorderColor, summaryRadius, summaryLayout,
      tileFillColor, tileBorderColor, tileScale,
      summaryFontPt, summaryRowPadPx,
      // Chart
      chartType, chartSide, chartSize, creditColor, debitColor, chartAlpha,
      // Tables
      tableScale, activityMode,
      activityFill, activityBorder, activityRadius,
      dailyFill, dailyBorder, dailyRadius,
      // NEW table options
      activityTableOpt, dailyTableOpt,
    };
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(payload));
      alert("Settings saved to this browser.");
    } catch (e) {
      console.error("Save failed", e);
      alert("Could not save settings. Your browser may be blocking storage.");
    }
  }

  function loadSettings() {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (!raw) { alert("No saved settings found."); return; }
      const s = JSON.parse(raw);
      // Rehydrate
      if (s.marketingImagePosition !== undefined) setMarketingImagePosition(s.marketingImagePosition);
      if (s.marketingRadius !== undefined) setMarketingRadius(s.marketingRadius);

      if (s.csBoxFillColor) setCsBoxFillColor(s.csBoxFillColor);
      if (s.csBoxBorderColor) setCsBoxBorderColor(s.csBoxBorderColor);
      if (s.csBoxRadius !== undefined) setCsBoxRadius(s.csBoxRadius);
      if (s.csColumnsCount !== undefined) setCsColumnsCount(s.csColumnsCount);
      if (s.csColumns) setCsColumns(s.csColumns);

      if (s.headerColor) setHeaderColor(s.headerColor);
      if (s.headerPt) setHeaderPt(s.headerPt);

      if (s.summaryFillColor) setSummaryFillColor(s.summaryFillColor);
      if (s.summaryBorderColor) setSummaryBorderColor(s.summaryBorderColor);
      if (s.summaryRadius !== undefined) setSummaryRadius(s.summaryRadius);
      if (s.summaryLayout) setSummaryLayout(s.summaryLayout);
      if (s.tileFillColor) setTileFillColor(s.tileFillColor);
      if (s.tileBorderColor) setTileBorderColor(s.tileBorderColor);
      if (s.tileScale) setTileScale(s.tileScale);
      if (s.summaryFontPt) setSummaryFontPt(s.summaryFontPt);
      if (s.summaryRowPadPx !== undefined) setSummaryRowPadPx(s.summaryRowPadPx);

      if (s.chartType) setChartType(s.chartType);
      if (s.chartSide) setChartSide(s.chartSide);
      if (s.chartSize) setChartSize(s.chartSize);
      if (s.creditColor) setCreditColor(s.creditColor);
      if (s.debitColor) setDebitColor(s.debitColor);
      if (s.chartAlpha) setChartAlpha(s.chartAlpha);

      if (s.tableScale) setTableScale(s.tableScale);
      if (s.activityMode) setActivityMode(s.activityMode);

      if (s.activityFill) setActivityFill(s.activityFill);
      if (s.activityBorder) setActivityBorder(s.activityBorder);
      if (s.activityRadius !== undefined) setActivityRadius(s.activityRadius);

      if (s.dailyFill) setDailyFill(s.dailyFill);
      if (s.dailyBorder) setDailyBorder(s.dailyBorder);
      if (s.dailyRadius !== undefined) setDailyRadius(s.dailyRadius);

      if (s.activityTableOpt) setActivityTableOpt(s.activityTableOpt);
      if (s.dailyTableOpt) setDailyTableOpt(s.dailyTableOpt);

      alert("Settings loaded.");
    } catch (e) {
      console.error("Load failed", e);
      alert("Could not load settings.");
    }
  }

  const Section = ({ title, children, defaultOpen = true }) => (
    // Use defaultOpen (uncontrolled) so it doesn't snap closed on input changes
    <details defaultOpen={defaultOpen} className="border rounded-lg">
      <summary className="cursor-pointer select-none px-3 py-2 font-semibold">{title}</summary>
      <div className="px-3 pb-3 pt-2 space-y-3">{children}</div>
    </details>
  );

  // Small reusable control row for table options
  
  // Simple row with fixed label column and flexible control area
  const FormRow = ({ label, children }) => (
    <div className="grid grid-cols-12 items-center gap-x-3 py-1.5">
      <div className="col-span-4 text-sm text-gray-700">{label}</div>
      <div className="col-span-8 min-w-0 flex items-center gap-2">{children}</div>
    </div>
  );

  const TableControls = ({ opts, setOpts }) => {
    const upd = (patch) => setOpts({ ...opts, ...patch });
    return (
      <div className="space-y-2.5 text-sm">
        <fieldset className="border rounded p-2 w-full">
          <legend className="px-1 text-[10px] uppercase tracking-wide text-gray-600">Colors</legend>
          <FormRow label="Header BG">
            <input type="color" value={opts.headerBg} onChange={(e) => upd({ headerBg: e.target.value })} />
          </FormRow>
          <FormRow label="Header Text">
            <input type="color" value={opts.headerFg} onChange={(e) => upd({ headerFg: e.target.value })} />
          </FormRow>
        </fieldset>

        
        <fieldset className="border rounded p-2 w-full">
          <legend className="px-1 text-[10px] uppercase tracking-wide text-gray-600">Header Line</legend>
          <FormRow label="Row Separation">
            <select className="border rounded px-2 py-1"
              value={opts.hdrSeparatorStyle} onChange={(e) => upd({ hdrSeparatorStyle: e.target.value })}>
              <option value="none">None</option>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
            </select>
          </FormRow>
          <FormRow label="Line Thickness">
            <input type="range" min={0} max={6} value={opts.hdrSeparatorThickness}
              onChange={(e) => upd({ hdrSeparatorThickness: Number(e.target.value) })} className="flex-1 max-w-full" />
            <span className="text-xs w-12 text-right">{opts.hdrSeparatorThickness}px</span>
          </FormRow>
          <FormRow label="Line Color">
            <input type="color" value={opts.hdrSeparatorColor} onChange={(e) => upd({ hdrSeparatorColor: e.target.value })} />
          </FormRow>
        </fieldset>

<fieldset className="border rounded p-2 w-full">
          <legend className="px-1 text-[10px] uppercase tracking-wide text-gray-600">Lines & Zebra</legend>
          <FormRow label="Row Separation">
            <select className="border rounded px-2 py-1"
              value={opts.separatorStyle} onChange={(e) => upd({ separatorStyle: e.target.value })}>
              <option value="none">None</option>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
            </select>
          </FormRow>
          <FormRow label="Line Thickness">
            <input type="range" min={0} max={6} value={opts.separatorThickness}
              onChange={(e) => upd({ separatorThickness: Number(e.target.value) })} className="flex-1 max-w-full" />
            <span className="text-xs w-12 text-right">{opts.separatorThickness}px</span>
          </FormRow>
          <FormRow label="Row Line">
            <input type="color" value={opts.separatorColor} onChange={(e) => upd({ separatorColor: e.target.value })} />
          </FormRow>
          <FormRow label="Zebra">
            <input type="checkbox" checked={opts.zebra} onChange={(e) => upd({ zebra: e.target.checked })} />
            <span>Enable Zebra Rows</span>
          </FormRow>
          <FormRow label="Zebra Color">
            <input type="color" value={opts.zebraColor} onChange={(e) => upd({ zebraColor: e.target.value })} />
          </FormRow>
          <FormRow label="Zebra Strength">
            <input type="range" min={0} max={1} step={0.05} value={opts.zebraOpacity}
              onChange={(e) => upd({ zebraOpacity: Number(e.target.value) })} className="flex-1 max-w-full" />
            <span className="text-xs w-12 text-right">{Math.round(opts.zebraOpacity * 100)}%</span>
          </FormRow>
        </fieldset>

        <fieldset className="border rounded p-2 w-full">
          <legend className="px-1 text-[10px] uppercase tracking-wide text-gray-600">Row Layout</legend>
          <FormRow label="Vertical Align">
            <select className="border rounded px-2 py-1"
              value={opts.vAlign} onChange={(e) => upd({ vAlign: e.target.value })}>
              <option value="top">Top</option>
              <option value="middle">Middle</option>
              <option value="bottom">Bottom</option>
            </select>
          </FormRow>
          <FormRow label="Density">
            <select className="border rounded px-2 py-1"
              value={opts.density} onChange={(e) => upd({ density: e.target.value })}>
              <option value="compact">Compact</option>
              <option value="cozy">Cozy</option>
              <option value="comfy">Comfy</option>
            </select>
          </FormRow>
          <FormRow label="Font Size">
            <input type="range" min={8} max={12} step={0.5} value={opts.fontPt || 10}
              onChange={(e) => upd({ fontPt: parseFloat(e.target.value) })} className="flex-1 max-w-full" />
            <span className="text-xs w-12 text-right">{(opts.fontPt ?? 10)}pt</span>
          </FormRow>
          <FormRow label="Row Padding">
            <input type="range" min={0} max={12} step={1} value={typeof opts.rowPadPx === "number" ? opts.rowPadPx : 6}
              onChange={(e) => upd({ rowPadPx: parseInt(e.target.value || "0", 10) })} className="flex-1 max-w-full" />
            <span className="text-xs w-12 text-right">{typeof opts.rowPadPx === "number" ? opts.rowPadPx : 6}px</span>
          </FormRow>
        </fieldset>
      </div>
    );
  };


  return (
    <div className="flex flex-col md:flex-row gap-6 p-4 min-h-screen bg-gray-100">
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .page-shell { box-shadow: none !important; border-radius: 0 !important; }
          .page-shell:not(:last-child) { break-after: page; page-break-after: always; }
          .bg-gray-100 { background-color: #ffffff !important; }
        }
      `}</style>

      {/* Controls */}
      
      <aside className="no-print w-full overflow-y-auto md:w-[600px] lg:w-[760px] pr-4">
        <div className="bg-white shadow p-4 rounded-lg space-y-4">
          <h2 className="text-xl font-bold">Layout Options</h2>

          <div className="grid gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
            <nav className="w-[260px] border rounded-lg overflow-hidden">
              {TAB_DEF.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${activeTab === t.key ? "bg-gray-100 font-semibold" : "hover:bg-gray-50"}`}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            <div className="min-w-0 space-y-3 text-[13px]">
              {activeTab === "header" && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold mb-1">Header (Logo & Return)</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const url = URL.createObjectURL(f);
                      setLogoSrc(url);
                    }}
                    className="block w-full text-sm"
                  />
                  <div className="text-xs text-gray-500">
                    Page 1 only. Wide logos use ≤2.0&quot;×0.5&quot; (address beneath). Tall/square logos use ≤0.75&quot; height with two-line address to the right (combined ≤2.0&quot; wide).
                  </div>
                </div>
              )}

              {activeTab === "mkt" && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold mb-1">Campaign Manager Image</div>
                  <div className="space-y-2 text-sm">
                    {["none", "top", "bottom", "both"].map((v) => (
                      <label key={v} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="mkpos"
                          value={v}
                          checked={marketingImagePosition === v}
                          onChange={(e) => setMarketingImagePosition(e.target.value)}
                        />
                        <span>
                          {v === "none"
                            ? "None"
                            : v === "top"
                            ? "Top Only"
                            : v === "bottom"
                            ? "Bottom Only"
                            : "Top Right + Bottom"}
                        </span>
                      </label>
                    ))}
                  </div>
                  <label className="text-sm flex items-center gap-2">
                    Corner radius:
                    <input
                      type="range"
                      min={0}
                      max={24}
                      value={marketingRadius}
                      onChange={(e) => setMarketingRadius(parseInt(e.target.value || "0", 10))}
                    />
                    <span className="text-xs w-6 text-right">{marketingRadius}px</span>
                  </label>
                </div>
              )}

              {activeTab === "cs" && (<div className="space-y-3">
                  <div className="text-sm font-semibold mb-1">Customer Service / Website</div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm flex items-center gap-2">
                      Fill <input type="color" value={csBoxFillColor} onChange={(e) => setCsBoxFillColor(e.target.value)} />
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      Border <input type="color" value={csBoxBorderColor} onChange={(e) => setCsBoxBorderColor(e.target.value)} />
                    </label>
                  </div>
                  <label className="text-sm flex items-center gap-2">
                    Radius
                    <input
                      type="range"
                      min={0}
                      max={24}
                      value={csBoxRadius}
                      onChange={(e) => setCsBoxRadius(parseInt(e.target.value || "0", 10))}
                    />
                    <span className="text-xs w-6 text-right">{csBoxRadius}px</span>
                  </label>
                
                  <div className="text-sm">
                    <span className="font-semibold mr-2">Columns</span>
                    {[1,2,3].map((n) => (
                      <label key={n} className="mr-3">
                        <input
                          type="radio"
                          name="csCols"
                          value={n}
                          checked={csColumnsCount === n}
                          onChange={(e) => setCsColumnsCount(parseInt(e.target.value, 10))}
                        /> {n}
                      </label>
                    ))}
                  </div>

                  {[0,1,2].slice(0, csColumnsCount).map((i) => (
                    <div key={i} className="grid grid-cols-2 gap-2 items-center">
                      <label className="text-xs text-gray-600">Label
                        <input
                          className="block w-full border rounded px-2 py-1 text-sm"
                          type="text"
                          value={csColumns[i]?.title ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCsColumns((prev) => {
                              const next = [...prev];
                              next[i] = { ...(next[i] || {title:"", value:""}), title: v };
                              return next;
                            });
                          }}
                        />
                      </label>
                      <label className="text-xs text-gray-600">Value
                        <input
                          className="block w-full border rounded px-2 py-1 text-sm"
                          type="text"
                          value={csColumns[i]?.value ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCsColumns((prev) => {
                              const next = [...prev];
                              next[i] = { ...(next[i] || {title:"", value:""}), value: v };
                              return next;
                            });
                          }}
                        />
                      </label>
                    </div>
                  ))}
    </div>)}

              {activeTab === "hdrsty" && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold mb-1">Section Header Style</div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm flex items-center gap-2">
                      Color <input type="color" value={headerColor} onChange={(e) => setHeaderColor(e.target.value)} />
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      Size
                      <input
                        type="range"
                        min={9}
                        max={14}
                        step={1}
                        value={headerPt}
                        onChange={(e) => setHeaderPt(parseInt(e.target.value || "11", 10))}
                      />
                      <span className="text-xs w-8 text-right">{headerPt}pt</span>
                    </label>
                  </div>
                </div>
              )}

              {activeTab === "summary" && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold mb-1">Account Summary</div>
                  <div className="text-sm">
                    <label className="mr-3">
                      <input
                        type="radio"
                        name="sumLayout"
                        value="cards"
                        checked={summaryLayout === "cards"}
                        onChange={(e) => setSummaryLayout(e.target.value)}
                      />{" "}
                      Cards
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="sumLayout"
                        value="condensed"
                        checked={summaryLayout === "condensed"}
                        onChange={(e) => setSummaryLayout(e.target.value)}
                      />{" "}
                      Condensed
                    </label>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm flex items-center gap-2">
                      Fill <input type="color" value={summaryFillColor} onChange={(e) => setSummaryFillColor(e.target.value)} />
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      Border <input type="color" value={summaryBorderColor} onChange={(e) => setSummaryBorderColor(e.target.value)} />
                    </label>
                  </div>
                  <label className="text-sm flex items-center gap-2">
                    Radius
                    <input
                      type="range"
                      min={0}
                      max={24}
                      value={summaryRadius}
                      onChange={(e) => setSummaryRadius(parseInt(e.target.value || "0", 10))}
                    />
                    <span className="text-xs w-6 text-right">{summaryRadius}px</span>
                  </label>
                  <label className="text-sm flex items-center gap-2">
                    Summary Font Size
                    <input type="range" min={8} max={12} step={0.5}
                      value={summaryFontPt}
                      onChange={(e) => setSummaryFontPt(parseFloat(e.target.value))}
                    />
                    <span className="text-xs w-12 text-right">{summaryFontPt}pt</span>
                  </label>
                  <label className="text-sm flex items-center gap-2">
                    Row Padding
                    <input type="range" min={0} max={16} step={1}
                      value={summaryRowPadPx}
                      onChange={(e) => setSummaryRowPadPx(parseInt(e.target.value || "0", 10))}
                    />
                    <span className="text-xs w-10 text-right">{summaryRowPadPx}px</span>
                  </label>

                  {summaryLayout === "cards" && (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <label className="text-sm flex items-center gap-2">
                          Tile Fill <input type="color" value={tileFillColor} onChange={(e) => setTileFillColor(e.target.value)} />
                        </label>
                        <label className="text-sm flex items-center gap-2">
                          Tile Border <input type="color" value={tileBorderColor} onChange={(e) => setTileBorderColor(e.target.value)} />
                        </label>
                      </div>
                      <label className="text-sm flex items-center gap-2">
                        Tile Size
                        <input
                          type="range"
                          min={0.8}
                          max={1.2}
                          step={0.05}
                          value={tileScale}
                          onChange={(e) => setTileScale(parseFloat(e.target.value))}
                        />
                        <span className="text-xs w-10 text-right">{tileScale.toFixed(2)}x</span>
                      </label>
                    </>
                  )}
                </div>
              )}

              {activeTab === "chart" && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold mb-1">Summary Chart (optional)</div>
                  <div className="text-sm">
                    <label className="mr-3">
                      <input
                        type="radio"
                        name="chartType"
                        value="none"
                        checked={chartType === "none"}
                        onChange={(e) => setChartType(e.target.value)}
                      />{" "}
                      None
                    </label>
                    <label className="mr-3">
                      <input
                        type="radio"
                        name="chartType"
                        value="donut"
                        checked={chartType === "donut"}
                        onChange={(e) => setChartType(e.target.value)}
                      />{" "}
                      Donut
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="chartType"
                        value="bar"
                        checked={chartType === "bar"}
                        onChange={(e) => setChartType(e.target.value)}
                      />{" "}
                      Bar
                    </label>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold mr-2">Side</span>
                    <label className="mr-3">
                      <input
                        type="radio"
                        name="chartSide"
                        value="right"
                        checked={chartSide === "right"}
                        onChange={(e) => setChartSide(e.target.value)}
                      />{" "}
                      Right
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="chartSide"
                        value="left"
                        checked={chartSide === "left"}
                        onChange={(e) => setChartSide(e.target.value)}
                      />{" "}
                      Left
                    </label>
                  </div>
                  <label className="text-sm flex items-center gap-2">
                    Size
                    <input
                      type="range"
                      min={120}
                      max={260}
                      step={10}
                      value={chartSize}
                      onChange={(e) => setChartSize(parseInt(e.target.value || "180", 10))}
                    />
                    <span className="text-xs w-10 text-right">{chartSize}px</span>
                  </label>
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm flex items-center gap-2">
                      Credits <input type="color" value={creditColor} onChange={(e) => setCreditColor(e.target.value)} />
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      Debits <input type="color" value={debitColor} onChange={(e) => setDebitColor(e.target.value)} />
                    </label>
                  </div>
                  <label className="text-sm flex items-center gap-2">
                    Transparency
                    <input
                      type="range"
                      min={0.2}
                      max={1}
                      step={0.05}
                      value={chartAlpha}
                      onChange={(e) => setChartAlpha(parseFloat(e.target.value))}
                    />
                    <span className="text-xs w-10 text-right">{Math.round(chartAlpha * 100)}%</span>
                  </label>
                </div>
              )}

              {activeTab === "activity" && (
                <div className="space-y-4">
                  <div className="text-sm font-semibold mb-1">Account Activity – Table Style</div>

                  <div className="text-sm -mt-2 mb-1">Layout</div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input type="radio" name="actLayout" value="debits-credits" checked={activityMode === "debits-credits"} onChange={() => setActivityMode("debits-credits")} />
                      Debits/Credits
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="radio" name="actLayout" value="amount-balance" checked={activityMode === "amount-balance"} onChange={() => setActivityMode("amount-balance")} />
                      Amount & Balance
                    </label>
                  </div>

                  {/* Activity box style controls */}
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <label className="text-sm flex items-center gap-2">Fill <input type="color" value={activityFill} onChange={(e) => setActivityFill(e.target.value)} /></label>
                    <label className="text-sm flex items-center gap-2">Border <input type="color" value={activityBorder} onChange={(e) => setActivityBorder(e.target.value)} /></label>
                  </div>
                  <label className="text-sm flex items-center gap-2">Radius
                    <input type="range" min={0} max={24} value={activityRadius} onChange={(e) => setActivityRadius(parseInt(e.target.value || "0", 10))} />
                    <span className="text-xs w-6 text-right">{activityRadius}px</span>
                  </label>
                  <TableControls opts={activityTableOpt} setOpts={setActivityTableOpt} />
                </div>
              )}
              {activeTab === "daily" && (
                <div className="space-y-4">
                  <div className="text-sm font-semibold mb-1">Daily Balance – Table Style</div>
                  {/* Daily box style controls */}
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <label className="text-sm flex items-center gap-2">Fill <input type="color" value={dailyFill} onChange={(e) => setDailyFill(e.target.value)} /></label>
                    <label className="text-sm flex items-center gap-2">Border <input type="color" value={dailyBorder} onChange={(e) => setDailyBorder(e.target.value)} /></label>
                  </div>
                  <label className="text-sm flex items-center gap-2">Radius
                    <input type="range" min={0} max={24} value={dailyRadius} onChange={(e) => setDailyRadius(parseInt(e.target.value || "0", 10))} />
                    <span className="text-xs w-6 text-right">{dailyRadius}px</span>
                  </label>
                  <TableControls opts={dailyTableOpt} setOpts={setDailyTableOpt} />
                </div>
              )}
              {activeTab === "export" && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold mb-1">Export / Settings</div>

                  <button
                    type="button"
                    onClick={exportToHTML}
                    className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Export to HTML (Letter 8.5×11)
                  </button>
                  <div className="text-xs text-gray-500">
                    The exported HTML centers each page on-screen and prints to Letter with your current settings.
                  </div>

                  <div className="pt-2 border-t" />
                  <div className="text-sm font-semibold">Save / Load Settings</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={saveSettings}
                      className="px-3 py-2 rounded border hover:bg-gray-50"
                    >
                      Save Settings
                    </button>
                    <button
                      type="button"
                      onClick={loadSettings}
                      className="px-3 py-2 rounded border hover:bg-gray-50"
                    >
                      Load Settings
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    Note: Logo image isn’t saved (browser object URLs aren’t persistent). Re-upload if needed.
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </aside>

      {/* Pages */}
      <main className="flex-1 min-w-0 space-y-8 flex flex-col items-end">
        <ActivityRowProbe />
        {pages}
      </main>
    </div>
  );
}